import type { Socket } from 'socket.io-client';
import type { Dataset } from './dataset.types';
import type { Pipe, PipeResponse } from './pipes.types';
import type { System } from './system.types';

export type PipesData = {
  data: Record<string, PipeResponse>;
};

export type SystemsData = {
  data: Record<string, System>;
};

export type DatasetsData = {
  data: Record<string, Dataset>;
};

export type SubscribeData = {
  data: Record<string, string>;
};

export const webSocketPipesEventMessages = [
  'pipes',
  'pipes_added',
  'pipes_deleted',
  'pipes_updated',
] as const;

export const webSocketSystemsEventMessages = [
  'systems',
  'systems_added',
  'systems_deleted',
  'systems_updated',
] as const;

export const webSocketDatasetsEventMessages = [
  'datasets',
  'datasets_added',
  'datasets_deleted',
  'datasets_updated',
] as const;

export type WebSocketPipesEventMessagesType = (typeof webSocketPipesEventMessages)[number];

export type WebSocketSystemsEventMessages = (typeof webSocketSystemsEventMessages)[number];

export type WebSocketDatasetsEventMessages = (typeof webSocketDatasetsEventMessages)[number];

export type ServerToClientPipesEvents = {
  [key in WebSocketPipesEventMessagesType]: (data: PipesData) => void;
};

export type ServerToClientSystemsEvents = {
  [key in WebSocketSystemsEventMessages]: (data: SystemsData) => void;
};

export type ServerToClientDatasetsEvents = {
  [key in WebSocketDatasetsEventMessages]: (data: DatasetsData) => void;
};

export type ServerToClientEvents = ServerToClientPipesEvents &
  ServerToClientSystemsEvents &
  ServerToClientDatasetsEvents;

export type ClientToServerEvents = {
  pipes: (data: Pipe[]) => void;
  get_pipes: (callback: (pipes: PipesData) => void) => void;
  subscribe_pipes: () => void;
  subscribe_systems: () => void;
  subscribe_datasets: () => void;
  get_systems: (callback: (pipes: SystemsData) => void) => void;
  get_datasets: (callback: (pipes: DatasetsData) => void) => void;
};

export type PipesMessages = {
  type: WebSocketPipesEventMessagesType;
  data: PipeResponse[];
};

export type SystemsMessages = {
  type: WebSocketSystemsEventMessages;
  data: System[];
};

export type DatasetsMessages = {
  type: WebSocketDatasetsEventMessages;
  data: Dataset[];
};

export type WebSocketEventMessages = PipesMessages | SystemsMessages | DatasetsMessages;

export type SocketIOErrorMessages =
  | 'xhr poll error'
  | 'token expired'
  | 'invalid token'
  | 'jwt malformed'
  | 'jwt must be provided';

export type SocketIOInstance = Socket<ServerToClientEvents, ClientToServerEvents>;

export type WebSocketFactory = (args: { wsUrl: string; jwt: string }) => SocketIOInstance;
